import "./InputSearch.scss";
import CategoryTag from "core/tags/category-tag/CategoryTag";
import DropdownLinkItem from "core/dropdown/DropdownLinkItem";
import { FormattedMessage, useIntl } from "react-intl";
import { QUERY_STATUS, SEARCH_TYPE } from "constants/constants";
import { useState, useEffect } from "react";
import _ from "lodash";
import LoaderV2 from "core/loader/LoaderV2";
import PropTypes from "prop-types";

const InputSearchPopup = ({
  defaultOptions,
  getSelectedOptions,
  hide,
  inputValue,
  limit,
  placeholder,
  readOnly,
  searchType,
  selectedOptions,
  setInputValue,
  title,
  type,
  loadDybList,
  inputSearchPopupMaxHeight,
  id,
  clearAllLabel,
  exclusiveValue,
  serverFiltered = false,
}) => {
  const intl = useIntl();
  const dybId = "dyb";
  const [localOptions, setLocalOptions] = useState(
    _.filter(
      defaultOptions,
      (elm) =>
        !_.chain(selectedOptions).map("value").includes(elm.value).value()
    )
  );
  const [selected, setSelected] = useState(selectedOptions || []);
  const normalize = (string = "") => _.deburr(string).toLowerCase();

  const unselectedOptions = () =>
    defaultOptions.filter(
      (elm) => !selected.some((s) => s.value === elm.value)
    );

  const filteredDefaultOptions = () =>
    defaultOptions.filter((elm) => {
      const label = normalize(elm?.label);
      const matchesAllTokens = normalize(inputValue)
        .split(" ")
        .filter(Boolean)
        .every((token) => label.includes(token));
      return matchesAllTokens && !selected.find((s) => s.value === elm.value);
    });

  // `serverFiltered`: the caller's search already matched server-side (fuzzy, so a
  // result need not contain the typed text) — re-filtering here would hide real matches.
  const optionsForInput = () =>
    serverFiltered ? unselectedOptions() : filteredDefaultOptions();

  useEffect(() => {
    if (inputValue?.length > 0) {
      setLocalOptions(id !== dybId ? optionsForInput() : defaultOptions);
    }
  }, [defaultOptions, inputValue]);

  const handleSearch = (e) => {
    setInputValue(e.target.value);
    setLocalOptions(optionsForInput());
  };

  const computeLocalOptions = (sel) =>
    defaultOptions.filter(
      (elm) => !(sel || []).some((option) => option.value === elm.value)
    );

  const applyExclusivity = (sel, clicked) => {
    if (exclusiveValue === undefined || exclusiveValue === null) return sel;
    if (clicked.value === exclusiveValue) {
      return sel.filter((item) => item.value === exclusiveValue);
    }
    return sel.filter((item) => item.value !== exclusiveValue);
  };

  const onClickOption = (e) => {
    if (e.selectAll) {
      const allReal = defaultOptions.filter((elm) => !elm.selectAll);
      setSelected(allReal);
      setLocalOptions([]);
      getSelectedOptions(allReal);
      return;
    }
    if (limit === 1) {
      setSelected([e]);
      setLocalOptions(localOptions.filter((elm) => !elm.value === e.value));
      getSelectedOptions([e]);
      hide();
    } else if (selected.length <= limit) {
      const next = applyExclusivity([...selected, { ...e }], e);
      setSelected(next);
      setLocalOptions(computeLocalOptions(next));
      getSelectedOptions(next);
    }
  };
  const onClearAll = () => {
    setSelected([]);
    setLocalOptions(defaultOptions);
    getSelectedOptions([]);
  };
  const onDeleteOption = (e, deleteOption) => {
    if (deleteOption) {
      const selectedOp = selected.filter((elm) => elm.value !== e.value);
      setSelected(selectedOp);
      setLocalOptions(
        defaultOptions.filter(
          (elm) => !_.chain(selectedOp).map("value").includes(elm.value).value()
        )
      );
      getSelectedOptions(selected.filter((elm) => elm.value !== e.value));
    }
  };

  const formatLocalTagsOptions = (options, deleteOption = false) => {
    return options?.map((e) => (
      <div className="input-search-popup-content-tag" key={e.value}>
        <CategoryTag
          isInputSearchPopup
          type={e.type || type}
          label={e.label}
          onClick={() => onClickOption(e)}
          deleteOption={deleteOption}
          onDelete={() => onDeleteOption(e, deleteOption)}
          color={e?.color}
        />
      </div>
    ));
  };
  const formatLocalListOptions = (options, deleteOption = false) =>
    options?.map((op, index) => (
      <DropdownLinkItem
        onClick={() => onClickOption(op)}
        deleteOption={deleteOption}
        onDelete={() => onDeleteOption(op, deleteOption)}
        id={index}
      >
        {op.label}
      </DropdownLinkItem>
    ));

  return (
    <div className="input-search-popup">
      <div className="input-search-popup-title">
        <FormattedMessage id={title.id} defaultMessage={title.defaultMessage} />
      </div>
      <div className="input-search-popup-input">
        <input
          type="text"
          className="input-search-popup-input-search"
          id="input-search-popup-input-search"
          value={inputValue}
          onChange={(e) => handleSearch(e)}
          disabled={readOnly}
          placeholder={
            placeholder ? intl.formatMessage(placeholder) : undefined
          }
          autoFocus
        />
      </div>
      {clearAllLabel && selected.length > 0 && (
        <button
          type="button"
          className="input-search-clear-all button-style"
          onClick={onClearAll}
        >
          {clearAllLabel}
        </button>
      )}
      {searchType === SEARCH_TYPE.TAG && selected.length > 0 && (
        <div className="padding-selected-tags">
          {formatLocalTagsOptions(selected, { deleteOption: true })}
        </div>
      )}
      {searchType === SEARCH_TYPE.ARRAY && selected.length > 0 && (
        <div>{formatLocalListOptions(selected, { deleteOption: true })}</div>
      )}

      <div className="input-search-popup-separator" />
      {loadDybList === QUERY_STATUS.LOADING ? (
        <LoaderV2 key="dyb_showcase_id" />
      ) : (
        <div>
          {searchType === SEARCH_TYPE.TAG && localOptions.length > 0 && (
            <div className="input-search-popup-content">
              {formatLocalTagsOptions(localOptions)}
            </div>
          )}
          {searchType === SEARCH_TYPE.ARRAY && localOptions.length > 0 && (
            <div
              className="input-search-list"
              style={
                inputSearchPopupMaxHeight && {
                  maxHeight: inputSearchPopupMaxHeight,
                }
              }
            >
              {formatLocalListOptions(localOptions)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

InputSearchPopup.propTypes = {
  defaultOptions: PropTypes.array,
  getSelectedOptions: PropTypes.func,
  hide: PropTypes.func,
  inputValue: PropTypes.string,
  limit: PropTypes.number,
  placeholder: PropTypes.object,
  readOnly: PropTypes.bool,
  searchType: PropTypes.string,
  selectedOptions: PropTypes.array,
  setInputValue: PropTypes.func,
  title: PropTypes.object,
  type: PropTypes.string,
  loadDybList: PropTypes.func,
  inputSearchPopupMaxHeight: PropTypes.number,
  id: PropTypes.string,
  clearAllLabel: PropTypes.string,
  exclusiveValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serverFiltered: PropTypes.bool,
};

export default InputSearchPopup;
